import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useIsMutating } from "@tanstack/react-query";
import { Backdrop, Box, Button, Card, CircularProgress, Stack, Typography, styled, type Theme } from "~/common/mui";
import { DesktopOnlyMessage } from "~/common/components/DesktopOnlyMessage/DesktopOnlyMessage";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { CONFIGURATOR_NAVIGATION_RESET_EVENT } from "../../application/configuratorNavigationReset";
import { useConfiguratorCommerceRuntime } from "../../application/useConfiguratorCommerceRuntime";
import { useConfiguratorTranslations } from "../../application/useConfiguratorTranslations";
import { useConfiguratorUpdate } from "../../application/useConfiguratorUpdate";
import { useConfiguratorAttachmentController } from "../../application/useConfiguratorAttachmentController";
import { configuratorMutationKey } from "../../data/configuratorConfigurationQuery";
import type { ReadyConfiguratorViewModel } from "../../application/useConfiguratorViewModel";
import { isReadyConfiguratorViewModel, useConfiguratorViewModel } from "../../application/useConfiguratorViewModel";
import type { ConfiguratorDefinition } from "../../contract/definition";
import type {
  ResolvedControlModel,
  ResolvedSubstepModel,
  ResolvedStepModel,
} from "../../engine/deriveConfiguratorModel";
import { buildConfiguratorPreviewViewModel } from "../preview/buildConfiguratorPreviewViewModel";
import { ConfiguratorPreview } from "../preview";
import { ConfiguratorSummary } from "../summary";
import {
  useConfiguratorSummaryController,
  type ConfiguratorSummaryController,
} from "../summary/useConfiguratorSummaryController";
import type { ConfiguratorCartSuccessRenderer } from "../summary/useConfiguratorSummaryInteractions";
import { ConfiguratorStep } from "../step";
import { ConfiguratorStepper } from "../stepper";
import { GoToSummary, GoToSummaryAction } from "../goToSummary/GoToSummary";
import type { ConfiguratorControlViewModel } from "../controls";
import { getRenderedControlValues } from "../controls/getRenderedControlValues";
import { SubstepRenderer, type ConfiguratorSubstepViewModel } from "../substeps";
import type { ConfiguratorAttachmentController } from "../substeps";

interface ConfiguratorTimelinePageProps {
  definition: ConfiguratorDefinition;
  renderCartSuccess: ConfiguratorCartSuccessRenderer;
  onRegistrationRequested: () => void;
}

interface ConfiguratorTimelinePageViewProps {
  title: string;
  viewModel: ReadyConfiguratorViewModel;
  initialStepperExpanded?: boolean;
  initialSummaryVisible?: boolean;
  initializationError?: Error | null;
  isUpdating?: boolean;
  mutationPending?: boolean;
  onControlChange?: (stepIndex: number, control: ResolvedControlModel, value: string | null) => void;
  onStepReset?: (stepIndex: number) => void;
  onStepNavigate?: (stepIndex: number) => void;
  onInitializationRetry?: () => void;
  translateStepName?: (key: string) => string;
  translateConfiguratorText?: (key: string) => string;
  summaryController: ConfiguratorSummaryController;
  summaryInteractionNode?: ReactNode;
  onCadOpened?: () => void;
  attachmentController?: ConfiguratorAttachmentController;
}

const PageRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "100%",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  backgroundColor: theme.palette.surfaceContainer.lowest,
}));

const PageGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: theme.spacing(3),
  alignItems: "start",
  [theme.breakpoints.up("desktop-l")]: {
    columnGap: theme.spacing(4),
  },
}));

const MainArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "stepperExpanded",
})<{ stepperExpanded: boolean }>(({ stepperExpanded, theme }) => ({
  gridColumn: "span 8",
  display: "grid",
  gridTemplateColumns: stepperExpanded ? "repeat(8, minmax(0, 1fr))" : "80px minmax(0, 1fr)",
  columnGap: theme.spacing(3),
  alignItems: "start",
  minWidth: 0,
  [theme.breakpoints.up("desktop-l")]: {
    columnGap: theme.spacing(4),
  },
}));

const getStickyRailStyles = (theme: Theme) => {
  const headerHeight = `var(--configurator-sticky-header-height, ${theme.spacing(0)})`;

  return {
    position: "sticky" as const,
    top: `calc(${headerHeight} + ${theme.spacing(2)})`,
    maxHeight: `calc(100dvh - ${headerHeight} - ${theme.spacing(4)})`,
    alignSelf: "start",
    overflow: "hidden",
  };
};

const StepperRail = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  ...getStickyRailStyles(theme),
  gridColumn: expanded ? "span 2" : undefined,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
}));

const Questions = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "stepperExpanded",
})<{ stepperExpanded: boolean }>(({ stepperExpanded }) => ({
  gridColumn: stepperExpanded ? "span 6" : undefined,
  minWidth: 0,
}));

const GoToSummarySection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ theme, visible }) => ({
  width: "100%",
  minHeight: theme.spacing(9),
  display: "flex",
  justifyContent: "center",
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? "auto" : "none",
  transition: theme.transitions.create("opacity", { duration: 180 }),
}));

const StickyGoToSummaryHost = styled(Box, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ theme, visible }) => ({
  position: "sticky",
  top: `calc(100dvh - ${theme.spacing(7)})`,
  zIndex: theme.zIndex.appBar,
  width: "100%",
  height: 0,
  marginBottom: `-${theme.spacing(2)}`,
  display: "flex",
  justifyContent: "center",
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? "auto" : "none",
  transition: theme.transitions.create("opacity", { duration: 180 }),
}));

const SummaryRail = styled(Box)(({ theme }) => ({
  ...getStickyRailStyles(theme),
  gridColumn: "span 4",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
}));

const SummaryArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "stepperExpanded",
})<{ stepperExpanded: boolean }>(({ stepperExpanded, theme }) => ({
  gridColumn: "span 12",
  display: "grid",
  gridTemplateColumns: stepperExpanded ? "repeat(12, minmax(0, 1fr))" : "80px minmax(0, 1fr)",
  columnGap: theme.spacing(3),
  alignItems: "start",
  minWidth: 0,
  [theme.breakpoints.up("desktop-l")]: {
    columnGap: theme.spacing(4),
  },
}));

const FullSummary = styled(Box, {
  shouldForwardProp: (prop) => prop !== "stepperExpanded",
})<{ stepperExpanded: boolean }>(({ stepperExpanded }) => ({
  gridColumn: stepperExpanded ? "span 10" : undefined,
  minWidth: 0,
}));

const EmptyDefinitionCard = styled(Card)(({ theme }) => ({
  gridColumn: "span 8",
  padding: theme.spacing(3),
  backgroundColor: theme.palette.surfaceContainer.low,
}));

const InitializationErrorCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  backgroundColor: theme.palette.error.container,
  color: theme.palette.error.onContainer,
}));

const getControlLabel = (control: ResolvedControlModel, t: TFunction) => {
  const cstic = control.context.instance.cstics[control.definition.cstic];
  return control.definition.labelKey ? t(control.definition.labelKey) : cstic.label ?? cstic.labelKey;
};

const getControlErrorText = (control: ResolvedControlModel, t: TFunction) => {
  if (control.error === "required") {
    return t("feature.configurator.v2.validation.required", "Please make a selection.");
  }
  if (control.error === "conflict") {
    return t("feature.configurator.v2.validation.conflict", "This selection conflicts with the configuration.");
  }
  return undefined;
};

const translationContent = (content: string) => (
  <Box
    className="scha__lc__external-content--shaft scha__lc__kits-table"
    dangerouslySetInnerHTML={{ __html: content }}
  />
);

const getOptionInfo = (value: { info?: string }, label: string, presentation?: "tooltip" | "richTooltip" | "dialog") =>
  value.info
    ? {
        content: translationContent(value.info),
        ariaLabel: `${label} information`,
        presentation,
      }
    : undefined;

const getControlInfo = (
  control: ResolvedControlModel,
  controlLabel: string,
  infoLinkLabel: string | undefined,
  translateConfiguratorText: (key: string) => string,
) => {
  const definition = control.definition;
  if (definition.infoDisplay) {
    return {
      content: translationContent(translateConfiguratorText(definition.infoDisplay.contentKey)),
      ariaLabel: infoLinkLabel ?? controlLabel,
      trigger: definition.infoDisplay.trigger,
      presentation: definition.infoDisplay.presentation,
      linkLabel: infoLinkLabel,
    };
  }

  const cstic = control.context.instance.cstics[definition.cstic];
  return cstic.info
    ? {
        content: translationContent(cstic.info),
        ariaLabel: controlLabel,
        trigger: "icon" as const,
        presentation: definition.infoPresentation ?? ("dialog" as const),
      }
    : undefined;
};

const getControlViewModel = (
  control: ResolvedControlModel,
  t: TFunction,
  forceDisabled: boolean,
  numberInputReadOnly: boolean,
  translateConfiguratorText: (key: string) => string,
): ConfiguratorControlViewModel | null => {
  const definition = control.definition;
  const cstic = control.context.instance.cstics[definition.cstic];
  const controlLabel = getControlLabel(control, t);
  const infoLinkLabel = definition.infoDisplay?.linkLabelKey
    ? translateConfiguratorText(definition.infoDisplay.linkLabelKey)
    : undefined;
  const renderedValues = getRenderedControlValues(
    cstic.values,
    "hideDisabled" in definition && definition.hideDisabled,
    definition.type === "TileSelect" && definition.sortSelectableFirst,
    "hiddenValues" in definition ? definition.hiddenValues : undefined,
  );
  const common = {
    controlIndex: control.controlIndex,
    label: controlLabel,
    hideLabel: definition.hideLabel,
    value: cstic.value,
    error: control.error,
    errorText: getControlErrorText(control, t),
    disabled: forceDisabled || control.disabled,
    info: getControlInfo(control, controlLabel, infoLinkLabel, translateConfiguratorText),
    helperText: definition.helperTextCstic
      ? control.context.instance.cstics[definition.helperTextCstic]?.value ?? undefined
      : undefined,
  };

  switch (definition.type) {
    case "Hero":
      return {
        ...common,
        type: "Hero",
        description: cstic.description,
        imageUrl: cstic.value ?? undefined,
      };
    case "TileSelect":
      return {
        ...common,
        type: "TileSelect",
        layout: definition.tileLayout,
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          description: value.description,
          info: getOptionInfo(value, value.label ?? value.labelKey, definition.infoPresentation),
          imageUrl: value.imageUrl,
          disabled: !value.selectable,
          standard: value.standard,
        })),
        standardLabel: t("feature.configurator.v2.standard", "standard"),
      };
    case "HorizontalCarousel":
      return {
        ...common,
        type: "HorizontalCarousel",
        layout: definition.tileLayout,
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          description: value.description,
          info: getOptionInfo(value, value.label ?? value.labelKey, definition.infoPresentation),
          imageUrl: value.imageUrl,
          disabled: !value.selectable,
          standard: value.standard,
        })),
        previousLabel: t("feature.configurator.v2.carousel.previous", {
          defaultValue: "Select the previous {{label}} option",
          label: common.label,
        }),
        nextLabel: t("feature.configurator.v2.carousel.next", {
          defaultValue: "Select the next {{label}} option",
          label: common.label,
        }),
        standardLabel: t("feature.configurator.v2.standard", "standard"),
      };
    case "DropdownSelect":
      return {
        ...common,
        type: "DropdownSelect",
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          description: value.description,
          info: getOptionInfo(value, value.label ?? value.labelKey, definition.infoPresentation),
          disabled: !value.selectable,
          standard: value.standard,
        })),
        placeholder: definition.placeholderKey ? translateConfiguratorText(definition.placeholderKey) : "",
        standardLabel: t("feature.configurator.v2.standard", "standard"),
      };
    case "NumberInput":
      return {
        ...common,
        type: "NumberInput",
        disabled: control.disabled || (forceDisabled && !numberInputReadOnly),
        readOnly: numberInputReadOnly && !control.disabled,
        placeholder: definition.placeholderKey ? translateConfiguratorText(definition.placeholderKey) : undefined,
        debounceMs: definition.debounceMs,
      };
    case "SelectChip":
      return {
        ...common,
        type: "SelectChip",
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          description: value.description,
          info: getOptionInfo(value, value.label ?? value.labelKey, definition.infoPresentation),
          disabled: !value.selectable,
          standard: value.standard,
        })),
        standardLabel: t("feature.configurator.v2.standard", "standard"),
      };
    case "CarouselSelect":
      return {
        ...common,
        type: "CarouselSelect",
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          description: value.description,
          info: getOptionInfo(value, value.label ?? value.labelKey, definition.infoPresentation),
          imageUrl: value.imageUrl,
          disabled: !value.selectable,
          standard: value.standard,
        })),
        previousLabel: t("feature.configurator.v2.carousel.previous", {
          defaultValue: "Select the previous {{label}} option",
          label: common.label,
        }),
        nextLabel: t("feature.configurator.v2.carousel.next", {
          defaultValue: "Select the next {{label}} option",
          label: common.label,
        }),
        standardLabel: t("feature.configurator.v2.standard", "standard"),
        showPreviewDescription: definition.showPreviewDescription,
      };
    case "TabSelect":
      return {
        ...common,
        type: "TabSelect",
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          disabled: !value.selectable,
        })),
      };
    case "StaticImageSelect":
      return {
        ...common,
        type: "StaticImageSelect",
        imageUrl: cstic.imageUrl,
        imageAlt: definition.imageAltKey ? translateConfiguratorText(definition.imageAltKey) : common.label,
        options: renderedValues.map((value) => ({
          value: value.value,
          label: value.label ?? value.labelKey,
          description: value.description,
          info: getOptionInfo(value, value.label ?? value.labelKey, definition.infoPresentation),
          disabled: definition.forceOptionsEnabled ? false : !value.selectable,
          standard: value.standard,
        })),
        placeholder: definition.placeholderKey ? translateConfiguratorText(definition.placeholderKey) : "",
        standardLabel: t("feature.configurator.v2.standard", "standard"),
      };
    case "Checkbox":
      return {
        ...common,
        type: "Checkbox",
        checkedValue: definition.checkedValue,
        uncheckedValue: definition.uncheckedValue,
      };
  }
};

const getSubstepViewModel = (
  substep: ResolvedSubstepModel,
  t: TFunction,
  forceDisabled: boolean,
  numberInputReadOnly: boolean,
  translateConfiguratorText: (key: string) => string,
): ConfiguratorSubstepViewModel => {
  const common = {
    substepIndex: substep.substepIndex,
    disabled: forceDisabled,
    title: substep.definition.titleKey ? t(substep.definition.titleKey) : undefined,
    description: substep.definition.descriptionKey ? t(substep.definition.descriptionKey) : undefined,
  };

  if (substep.renderer === "carouselColumns") {
    return {
      ...common,
      renderer: "carouselColumns",
      columns: substep.columns.map((column) => {
        const carousel = getControlViewModel(
          column.carousel,
          t,
          forceDisabled,
          numberInputReadOnly,
          translateConfiguratorText,
        );
        if (!carousel || carousel.type !== "CarouselSelect") {
          throw new Error("A carouselColumns Substep must resolve a CarouselSelect Control.");
        }

        return {
          carousel,
          details: column.details
            .filter((control) => control.visible)
            .map((control) =>
              getControlViewModel(control, t, forceDisabled, numberInputReadOnly, translateConfiguratorText),
            )
            .filter((control): control is ConfiguratorControlViewModel => control !== null),
        };
      }),
    };
  }

  if (substep.renderer === "configurationAttachment") {
    const formatTextKey = substep.definition.formatTextKey;
    const translatedFormatText = formatTextKey ? translateConfiguratorText(formatTextKey) : undefined;

    return {
      ...common,
      renderer: "configurationAttachment",
      title: substep.definition.titleKey ? translateConfiguratorText(substep.definition.titleKey) : undefined,
      formatText: translatedFormatText !== formatTextKey ? translatedFormatText : undefined,
      attachment: substep.attachment,
    };
  }

  return {
    ...common,
    renderer: "generic",
    controlsLayout: substep.definition.controlsLayout,
    controls: substep.controls
      .filter((control) => control.visible)
      .map((control) => getControlViewModel(control, t, forceDisabled, numberInputReadOnly, translateConfiguratorText))
      .filter((control): control is ConfiguratorControlViewModel => control !== null),
  };
};

type InitialExpansionMode = "start" | "load";

const defaultStepExpansion = (step: ResolvedStepModel, mode: InitialExpansionMode) =>
  mode === "start" || (step.status !== "completed" && step.status !== "disabled");

const createExpandedSteps = (steps: readonly ResolvedStepModel[], mode: InitialExpansionMode) =>
  Object.fromEntries(steps.map((step) => [step.stepIndex, defaultStepExpansion(step, mode)]));

const useExpandedSteps = (steps: readonly ResolvedStepModel[], mode: InitialExpansionMode, revision: number) => {
  const [expansionState, setExpansionState] = useState(() => ({
    expandedSteps: createExpandedSteps(steps, mode),
    mode,
    revision,
  }));

  useEffect(() => {
    setExpansionState((current) => {
      if (current.revision !== revision) {
        return {
          expandedSteps: createExpandedSteps(steps, mode),
          mode,
          revision,
        };
      }

      const expandedSteps = { ...current.expandedSteps };
      steps.forEach((step) => {
        if (expandedSteps[step.stepIndex] === undefined) {
          expandedSteps[step.stepIndex] = defaultStepExpansion(step, mode);
        }
      });
      return { ...current, expandedSteps };
    });
  }, [mode, revision, steps]);

  const toggleStep = (stepIndex: number) =>
    setExpansionState((current) => ({
      ...current,
      expandedSteps: {
        ...current.expandedSteps,
        [stepIndex]: !current.expandedSteps[stepIndex],
      },
    }));
  const expandStep = (stepIndex: number) =>
    setExpansionState((current) => ({
      ...current,
      expandedSteps: { ...current.expandedSteps, [stepIndex]: true },
    }));

  return {
    expandedSteps:
      expansionState.revision === revision ? expansionState.expandedSteps : createExpandedSteps(steps, mode),
    expandStep,
    toggleStep,
  };
};

const getMissingControlCount = (viewModel: ReadyConfiguratorViewModel) => {
  const missingControlCount = viewModel.steps.reduce(
    (count, step) =>
      !step.visible
        ? count
        : count +
          step.substeps.reduce(
            (substepCount, substep) =>
              substepCount +
              substep.controls.filter(
                (control) => control.visible && !control.disabled && !control.complete && control.error !== "conflict",
              ).length,
            0,
          ),
    0,
  );

  return viewModel.complete ? 0 : Math.max(missingControlCount, 1);
};

const getVisibleStepIndex = (steps: readonly ResolvedStepModel[], activeStepIndex: number) => {
  const visibleSteps = steps.filter((step) => step.visible);
  const exactIndex = visibleSteps.findIndex((step) => step.stepIndex === activeStepIndex);
  if (exactIndex >= 0) {
    return exactIndex;
  }

  const nextIndex = visibleSteps.findIndex((step) => step.stepIndex > activeStepIndex);
  if (nextIndex >= 0) {
    return nextIndex;
  }

  return visibleSteps.length > 0 ? visibleSteps.length - 1 : null;
};

export const ConfiguratorTimelinePageView = ({
  title,
  viewModel,
  initialStepperExpanded = true,
  initialSummaryVisible = false,
  initializationError,
  isUpdating = false,
  onControlChange,
  onStepReset,
  onStepNavigate,
  onInitializationRetry,
  translateStepName,
  translateConfiguratorText,
  summaryController,
  summaryInteractionNode,
  onCadOpened,
  attachmentController,
  mutationPending: externallyPending = false,
}: ConfiguratorTimelinePageViewProps) => {
  const { t } = useTranslation();
  const stepName = translateStepName ?? ((key: string) => t(key, { defaultValue: key }));
  const configuratorText = translateConfiguratorText ?? ((key: string) => t(key, { defaultValue: key }));
  const actionsDisabled = isUpdating || externallyPending;
  const [stepperExpanded, setStepperExpanded] = useState(initialStepperExpanded);
  const [summaryVisible, setSummaryVisible] = useState(initialSummaryVisible);
  const [goToSummaryVisible, setGoToSummaryVisible] = useState(true);
  const initialExpansionMode = viewModel.initialExpansion?.mode ?? "start";
  const initialExpansionRevision = viewModel.initialExpansion?.revision ?? 0;
  const { expandedSteps, expandStep, toggleStep } = useExpandedSteps(
    viewModel.steps,
    initialExpansionMode,
    initialExpansionRevision,
  );
  const visibleSteps = useMemo(() => viewModel.steps.filter((step) => step.visible), [viewModel.steps]);
  const activeVisibleStepIndex = getVisibleStepIndex(viewModel.steps, viewModel.activeStepIndex);
  const stepElements = useRef<Array<HTMLDivElement | null>>([]);
  const goToSummarySectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (summaryVisible) {
      setGoToSummaryVisible(true);
      return;
    }

    const section = goToSummarySectionRef.current;
    if (!section || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setGoToSummaryVisible(entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(section);

    return () => observer.disconnect();
  }, [summaryVisible]);

  const navigateToStep = useCallback(
    (stepIndex: number) => {
      setSummaryVisible(false);
      expandStep(stepIndex);
      onStepNavigate?.(stepIndex);
      window.requestAnimationFrame(() => {
        stepElements.current[stepIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [expandStep, onStepNavigate],
  );

  useEffect(() => {
    const navigateToFirstStep = () => navigateToStep(visibleSteps[0]?.stepIndex ?? 0);
    window.addEventListener(CONFIGURATOR_NAVIGATION_RESET_EVENT, navigateToFirstStep);

    return () => window.removeEventListener(CONFIGURATOR_NAVIGATION_RESET_EVENT, navigateToFirstStep);
  }, [navigateToStep, visibleSteps]);

  const previewViewModel = buildConfiguratorPreviewViewModel(viewModel.configuration, viewModel.selections, {
    translate: (key) => t(key),
    translateStepName: stepName,
    standardLabel: t("feature.configurator.v2.standard", "standard"),
  });
  return (
    <PageRoot aria-label={title}>
      <PageGrid>
        {visibleSteps.length === 0 ? (
          <EmptyDefinitionCard>
            <Typography variant="bodyMedium">
              {t(
                "feature.configurator.v2.definitionPending",
                "The configurator is connected. Configuration steps have not been defined yet.",
              )}
            </Typography>
          </EmptyDefinitionCard>
        ) : (
          <>
            {summaryVisible ? (
              <SummaryArea stepperExpanded={stepperExpanded}>
                <StepperRail data-configurator-rail="steps" expanded={stepperExpanded}>
                  <ConfiguratorStepper
                    expanded={stepperExpanded}
                    steps={visibleSteps.map((step) => ({
                      title: stepName(step.definition.titleKey),
                      status: step.status === "disabled" ? "disabled" : "completed",
                    }))}
                    activeStep={null}
                    onExpandedChange={setStepperExpanded}
                    onStepClick={(visibleStepIndex) =>
                      navigateToStep(visibleSteps[visibleStepIndex]?.stepIndex ?? visibleStepIndex)
                    }
                    sx={{ flex: 1 }}
                  />
                </StepperRail>

                <FullSummary stepperExpanded={stepperExpanded}>
                  <ConfiguratorSummary
                    controller={summaryController}
                    onEditSection={(sectionKey) => navigateToStep(Number(sectionKey))}
                    onCadOpened={onCadOpened}
                  />
                </FullSummary>
              </SummaryArea>
            ) : (
              <>
                <MainArea stepperExpanded={stepperExpanded}>
                  <StepperRail data-configurator-rail="steps" expanded={stepperExpanded}>
                    <ConfiguratorStepper
                      expanded={stepperExpanded}
                      steps={visibleSteps.map((step) => ({
                        title: stepName(step.definition.titleKey),
                        status: step.status,
                      }))}
                      activeStep={activeVisibleStepIndex}
                      onExpandedChange={setStepperExpanded}
                      onStepClick={(visibleStepIndex) =>
                        navigateToStep(visibleSteps[visibleStepIndex]?.stepIndex ?? visibleStepIndex)
                      }
                      sx={{ flex: 1 }}
                    />
                  </StepperRail>

                  <Questions key={viewModel.configuration.configId} stepperExpanded={stepperExpanded} gap={4}>
                    {initializationError ? (
                      <InitializationErrorCard>
                        <Typography variant="bodyMedium">
                          {t(
                            "feature.configurator.v2.initializationError",
                            "The next configuration step could not be prepared.",
                          )}
                        </Typography>
                        <Button variant="text" disabled={actionsDisabled} onClick={onInitializationRetry}>
                          {t("feature.configurator.v2.retryInitialization", "Retry")}
                        </Button>
                      </InitializationErrorCard>
                    ) : null}
                    <StickyGoToSummaryHost
                      visible={viewModel.complete && !goToSummaryVisible}
                      aria-hidden={!(viewModel.complete && !goToSummaryVisible)}
                    >
                      <GoToSummaryAction
                        viewModel={{
                          unfilledControlCount: getMissingControlCount(viewModel),
                          conflictCount: viewModel.configuration.conflicts.length,
                          scrollState: "collapsed",
                        }}
                        disabled={actionsDisabled}
                        onGoToSummary={() => setSummaryVisible(true)}
                      />
                    </StickyGoToSummaryHost>
                    {visibleSteps.map((step) => {
                      const expanded =
                        expandedSteps[step.stepIndex] ?? defaultStepExpansion(step, initialExpansionMode);
                      const controlsDisabled = actionsDisabled || !step.editable;
                      const numberInputReadOnly = actionsDisabled && step.editable;

                      return (
                        <Box
                          key={step.stepIndex}
                          ref={(element: HTMLDivElement | null) => {
                            stepElements.current[step.stepIndex] = element;
                          }}
                          sx={{ scrollMarginTop: 24 }}
                        >
                          <ConfiguratorStep
                            title={stepName(step.definition.titleKey)}
                            status={step.status}
                            expanded={expanded}
                            error={step.error}
                            completedLabel={t("feature.configurator.v2.step.completedLabel", "Completed")}
                            incompleteMessage={t(
                              "feature.configurator.v2.step.incomplete",
                              "You are missing some mandatory selections.",
                            )}
                            incompleteLabel={t("feature.configurator.v2.step.incompleteLabel", "Incomplete")}
                            errorMessage={t(
                              "feature.configurator.v2.step.error",
                              "Your selections have errors, please select correct values.",
                            )}
                            errorLabel={t("feature.configurator.v2.step.errorLabel", "There is an issue")}
                            disabledMessage={t(
                              "feature.configurator.v2.step.disabled",
                              "This step isn't required for your current configuration",
                            )}
                            expandLabel={t("feature.configurator.v2.step.expand", {
                              defaultValue: "Expand {{title}}",
                              title: stepName(step.definition.titleKey),
                            })}
                            collapseLabel={t("feature.configurator.v2.step.collapse", {
                              defaultValue: "Collapse {{title}}",
                              title: stepName(step.definition.titleKey),
                            })}
                            onToggle={() => toggleStep(step.stepIndex)}
                          >
                            {step.substeps
                              .filter((substep) => substep.visible)
                              .map((substep) => (
                                <SubstepRenderer
                                  key={`${substep.context.instance.kmatType}-${substep.context.logicalIndex}-${substep.substepIndex}`}
                                  viewModel={getSubstepViewModel(
                                    substep,
                                    t,
                                    controlsDisabled,
                                    numberInputReadOnly,
                                    configuratorText,
                                  )}
                                  attachmentController={attachmentController}
                                  onControlChange={(change) => {
                                    const control = substep.controls.find(
                                      (candidate) => candidate.controlIndex === change.controlIndex,
                                    );
                                    if (control) {
                                      onControlChange?.(step.stepIndex, control, change.value);
                                    }
                                  }}
                                />
                              ))}
                            <Button
                              variant="text"
                              disabled={controlsDisabled}
                              onClick={() => onStepReset?.(step.stepIndex)}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              {t("feature.configurator.v2.step.reset", "Reset step")}
                            </Button>
                          </ConfiguratorStep>
                        </Box>
                      );
                    })}

                    <GoToSummarySection
                      ref={goToSummarySectionRef}
                      visible={goToSummaryVisible}
                      aria-hidden={!goToSummaryVisible}
                    >
                      <GoToSummary
                        viewModel={{
                          unfilledControlCount: getMissingControlCount(viewModel),
                          conflictCount: viewModel.configuration.conflicts.length,
                          scrollState: "expanded",
                        }}
                        disabled={actionsDisabled}
                        onGoToSummary={() => setSummaryVisible(true)}
                      />
                    </GoToSummarySection>
                  </Questions>
                </MainArea>

                <SummaryRail data-configurator-rail="summary">
                  <ConfiguratorPreview viewModel={previewViewModel} sx={{ height: "100%", flex: 1 }} />
                </SummaryRail>
              </>
            )}
          </>
        )}
      </PageGrid>
      {summaryInteractionNode}

      <Backdrop open={isUpdating} sx={(theme) => ({ color: theme.palette.primary.main, zIndex: theme.zIndex.modal })}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </PageRoot>
  );
};

interface ConfiguratorTimelinePageStatusViewProps {
  status: "loading" | "unavailable";
}

export const ConfiguratorTimelinePageStatusView = ({ status }: ConfiguratorTimelinePageStatusViewProps) => {
  const { t } = useTranslation();

  if (status === "loading") {
    return <CircleSpinner />;
  }

  return (
    <Typography variant="bodyMedium">
      {t("feature.configurator.v2.unavailable", "The configurator is currently unavailable.")}
    </Typography>
  );
};

const DesktopConfiguratorTimelinePage = ({
  definition,
  renderCartSuccess,
  onRegistrationRequested,
}: ConfiguratorTimelinePageProps) => {
  const { t } = useTranslation();
  const { t: xmlT } = useConfiguratorTranslations(definition.productCode);
  const viewModel = useConfiguratorViewModel(definition);
  const translateConfiguratorText = (key: string) => {
    const translated = xmlT?.(key, { defaultValue: key });
    return translated && translated !== key ? translated : t(key, { defaultValue: key });
  };

  if (!isReadyConfiguratorViewModel(viewModel)) {
    return <ConfiguratorTimelinePageStatusView status={viewModel.isLoading ? "loading" : "unavailable"} />;
  }

  return (
    <ConnectedConfiguratorTimelinePage
      definition={definition}
      viewModel={viewModel}
      translateConfiguratorText={translateConfiguratorText}
      renderCartSuccess={renderCartSuccess}
      onRegistrationRequested={onRegistrationRequested}
    />
  );
};

const ConnectedConfiguratorTimelinePage = ({
  definition,
  viewModel,
  translateConfiguratorText,
  renderCartSuccess,
  onRegistrationRequested,
}: ConfiguratorTimelinePageProps & {
  viewModel: ReadyConfiguratorViewModel;
  translateConfiguratorText: (key: string) => string;
}) => {
  const { t } = useTranslation();
  const configuration = viewModel.configuration;
  const attachmentController = useConfiguratorAttachmentController(configuration);
  const attachmentMutationPending =
    useIsMutating({ mutationKey: configuratorMutationKey(configuration.productCode) }) > 0 ||
    attachmentController.viewModel.pendingOperation !== null;
  const commerce = useConfiguratorCommerceRuntime(configuration);
  const { initializationError, initializeStep, isUpdating, resetStep, retryInitialization, updateControl } =
    useConfiguratorUpdate(definition, configuration);
  const nextInitializationStepIndex = viewModel.steps.find(
    (step) => step.visible && step.dependencyUnlocked && !step.reached,
  )?.stepIndex;

  useEffect(() => {
    if (nextInitializationStepIndex === undefined || isUpdating || attachmentMutationPending || initializationError) {
      return;
    }

    initializeStep(nextInitializationStepIndex);
  }, [attachmentMutationPending, initializationError, initializeStep, isUpdating, nextInitializationStepIndex]);
  const previewViewModel = buildConfiguratorPreviewViewModel(configuration, viewModel.selections, {
    translate: (key) => t(key),
    translateStepName: translateConfiguratorText,
    standardLabel: t("feature.configurator.v2.standard", "standard"),
  });
  const summary = useConfiguratorSummaryController({
    configurator: viewModel,
    preview: previewViewModel,
    commerce,
    isUpdating: isUpdating || attachmentMutationPending,
    renderCartSuccess,
    onRegistrationRequested,
  });

  return (
    <ConfiguratorTimelinePageView
      title={t(definition.titleKey)}
      viewModel={viewModel}
      translateStepName={translateConfiguratorText}
      translateConfiguratorText={translateConfiguratorText}
      initializationError={initializationError}
      isUpdating={isUpdating}
      mutationPending={attachmentMutationPending}
      summaryController={summary.controller}
      summaryInteractionNode={summary.interactionNode}
      onCadOpened={summary.trackCadOpened}
      onInitializationRetry={retryInitialization}
      attachmentController={attachmentController}
      onControlChange={(stepIndex, control, value) => {
        commerce.clearSubmission();
        void updateControl(stepIndex, control, value);
      }}
      onStepReset={(stepIndex) => {
        commerce.clearSubmission();
        void resetStep(stepIndex);
      }}
    />
  );
};

export const ConfiguratorTimelinePage = ({
  definition,
  renderCartSuccess,
  onRegistrationRequested,
}: ConfiguratorTimelinePageProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  if (!isDesktop) {
    return (
      <DesktopOnlyMessage>
        {t(
          "feature.configurator.v2.desktopRequired",
          "This configurator is available on desktop screens. Please open it on a desktop device.",
        )}
      </DesktopOnlyMessage>
    );
  }

  return (
    <DesktopConfiguratorTimelinePage
      definition={definition}
      renderCartSuccess={renderCartSuccess}
      onRegistrationRequested={onRegistrationRequested}
    />
  );
};
